import type { DirectorDocumentItem, DirectorRequestDetail } from '@/types/director'
import { formatDeadline } from '@/utils/caseList'
import { documentWorkOpen } from '@/utils/documentDue'
import { FileText, BellRing, Download, Eye, Inbox, Ban } from 'lucide-react'

interface CaseDocumentsBlockProps {
  detail: DirectorRequestDetail
  canRequestDocs: boolean
  isRequestingDocs: boolean
  onRequestDocs: () => void
  isCancellingDocs?: boolean
  onCancelDocs?: () => void
  onPreview: (doc: DirectorDocumentItem) => void
  onDownload: (doc: DirectorDocumentItem) => void
  onRemind: () => void
  remindCooldownUntil: string | null
  isReminding: boolean
}

function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return ''
  const units = ['B', 'KB', 'MB', 'GB']
  let i = 0
  let v = bytes
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024
    i++
  }
  return `${Math.round(v)} ${units[i]}`
}

/** Fallakte v2 (P5.6): Dokumenten-Block mit Status, Vorschau, Anforderung, Reminder. */
export function CaseDocumentsBlock({
  detail, canRequestDocs, isRequestingDocs, onRequestDocs,
  isCancellingDocs = false, onCancelDocs,
  onPreview, onDownload, onRemind, remindCooldownUntil, isReminding,
}: CaseDocumentsBlockProps) {
  const hasFile = Boolean(detail.has_document ?? detail.documents_received)
  const documentsOpen = documentWorkOpen(
    Boolean(detail.document_due), detail.documents_requested_at, hasFile,
  )
  const cooldownText = formatDeadline(remindCooldownUntil)

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4">
      <div className="flex items-center justify-between mb-3 gap-2">
        <h2 className="text-sm font-bold">Dokumente</h2>
        <div className="flex flex-wrap gap-1.5 text-[11px]">
          {hasFile ? (
            <span className="px-2 py-0.5 rounded-full bg-green-100 text-green-800 font-medium">Eingegangen</span>
          ) : documentsOpen ? (
            <span className="px-2 py-0.5 rounded-full bg-orange-100 text-orange-800 font-medium">Dokumente ausstehend</span>
          ) : null}
        </div>
      </div>

      {detail.documents && detail.documents.length > 0 ? (
        <ul className="space-y-1.5 mb-3" aria-label="Eingereichte Dokumente">
          {detail.documents.map(d => (
            <li key={d.id} className="flex items-center justify-between gap-2 text-sm">
              <span className="flex items-center gap-2 min-w-0">
                <FileText className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                <span className="truncate pr-2" title={d.filename}>{d.filename}</span>
                {formatBytes(d.file_size) && (
                  <span className="text-[10px] text-gray-400 shrink-0">{formatBytes(d.file_size)}</span>
                )}
              </span>
              <span className="flex items-center gap-3 shrink-0">
                <button type="button" onClick={() => onPreview(d)}
                  className="flex items-center gap-1 text-blue-600 hover:underline text-xs">
                  <Eye className="w-3 h-3" /> Vorschau
                </button>
                <button type="button" onClick={() => onDownload(d)}
                  className="flex items-center gap-1 text-blue-600 hover:underline text-xs">
                  <Download className="w-3 h-3" /> Download
                </button>
              </span>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-sm text-gray-500 mb-3 flex items-center gap-1.5">
          <Inbox className="w-3.5 h-3.5" /> Noch keine Dokumente eingereicht.
        </p>
      )}

      {detail.documents_requested_at && (
        <p className="text-xs text-orange-700 mb-2">
          Angefordert am {new Date(detail.documents_requested_at).toLocaleDateString('de-CH')} —
          die Lehrperson wurde per E-Mail informiert.
        </p>
      )}

      {documentsOpen && (
        <div className="rounded-md bg-orange-50 border border-orange-200 px-3 py-2 mb-3 text-xs text-orange-900 leading-relaxed">
          Fehlendes Dokument stoppt die Kaskade nicht. Zahler und Freigabe sind trotzdem möglich.
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={onRequestDocs}
          disabled={!canRequestDocs || isRequestingDocs}
          title={canRequestDocs ? undefined : 'Erfordert eine Rechte-Freigabe durch den Superadmin.'}
          className="px-3 py-1.5 border border-gray-300 rounded-lg hover:bg-gray-50 text-xs font-medium disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isRequestingDocs ? 'Wird angefragt…' : 'Dokumente anfordern'}
        </button>
        {documentsOpen && (
          <button
            type="button"
            onClick={onRemind}
            disabled={!canRequestDocs || isReminding || Boolean(remindCooldownUntil)}
            title={
              remindCooldownUntil
                ? `Erinnerung bereits gesendet — nächste möglich ab ${cooldownText}`
                : 'Erinnerungs-E-Mail an die Lehrperson senden (max. alle 24 h)'
            }
            className="flex items-center gap-1.5 px-3 py-1.5 border border-orange-300 text-orange-700 rounded-lg hover:bg-orange-50 text-xs font-medium disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <BellRing className="w-3.5 h-3.5" />
            {remindCooldownUntil ? `Erinnerung möglich ab ${cooldownText}` : 'Erinnerung senden'}
          </button>
        )}
        {Boolean(detail.documents_requested_at) && !hasFile && onCancelDocs && (
          <button
            type="button"
            onClick={onCancelDocs}
            disabled={!canRequestDocs || isCancellingDocs}
            title={canRequestDocs ? 'Dokumentenanfrage zurücknehmen — die Lehrperson muss nichts mehr einreichen.' : 'Erfordert eine Rechte-Freigabe durch den Superadmin.'}
            className="flex items-center gap-1.5 px-3 py-1.5 border border-red-200 text-red-700 rounded-lg hover:bg-red-50 text-xs font-medium disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Ban className="w-3.5 h-3.5" />
            {isCancellingDocs ? 'Wird storniert…' : 'Dokumentenanfrage stornieren'}
          </button>
        )}
      </div>
    </div>
  )
}
